import { defineStore } from "pinia";
import { computed, ref, Ref, watch } from "vue";
import axios from "axios";
import Event from "@/types/Event";
import Sector from "@/types/Sector";
import Section from "@/types/Section";
import Row from "@/types/Mapping/Row";
import Seat from "@/types/Mapping/Seat";
import TicketType from "@/types/TicketType";
import holdSeat, { type HoldResponse } from "@/api/requests/holdSeat";
import releaseSeat from "@/api/requests/releaseSeat";
import getHeldSeats from "@/api/requests/getHeldSeats";
import { useEventsStore } from "./events";
import { useUserStore } from "./user";
import NominalTicket from "@/types/NominalTicket";
import {
  cartTotals,
  generalMax,
  groupLines,
  reservationSectors,
  ticketsFromLines,
  ticketsOf,
  type CartGroup,
  type CartLine,
  type CartTotals,
  type GeneralPick,
} from "@/utils/ticketCart";

/** Why the Cart can't take more tickets: the buyer's cap for the event, or the type's limit per buyer. */
export type Refusal = "full" | "limit";

/** `taken` when someone else holds the seat. */
export type AddSeatResult = "added" | "taken" | Refusal;

export type AddGeneralResult = "added" | Refusal;

/** A 409, or a 422 naming the seat: someone else holds it. */
function isTaken(error: unknown, seat: Seat): boolean {
  if (!axios.isAxiosError(error)) return false;

  const status = error.response?.status;
  const named = (error.response?.data as { seats?: string[] } | undefined)?.seats ?? [];

  return status === 409 || (status === 422 && named.includes(seat.id));
}

/** The Cart of the mapped purchase flow: tickets picked one by one, seats held as they are picked. */
export const useTicketCartStore = defineStore("ticketCart", () => {
  const lines: Ref<CartLine[]> = ref([]);

  /** When the held seats are released, unless the holds change again. */
  const expiresAt: Ref<string | null> = ref(null);

  /** The event the Cart was filled on. */
  const eventId: Ref<string | null> = ref(null);

  /** Seats waiting on a hold call, with the type they were picked as. */
  const waiting: Ref<{ seatId: string; typeId: string }[]> = ref([]);

  /** Ids of the seats waiting on a hold call. */
  const pending = computed<string[]>(() => waiting.value.map(w => w.seatId));

  const count = computed<number>(() => lines.value.length);

  /** Whether any ticket in the Cart has a held seat. */
  const holdsSeats = computed<boolean>(() => lines.value.some(line => line.seat !== undefined));

  /** The lines by Section, as the Cart lists them. */
  const groups = computed<CartGroup[]>(() => groupLines(lines.value));

  const totals = computed<CartTotals>(() => {
    const selected = useEventsStore().selected;
    return selected === null ? { subtotal: 0, fees: 0 } : cartTotals(lines.value, selected);
  });

  const subtotal = computed<number>(() => totals.value.subtotal);

  const fees = computed<number>(() => totals.value.fees);

  /** Tells apart General tickets added in the same instant. crypto.randomUUID isn't there over plain HTTP. */
  let added = 0;

  /** Hold calls run one at a time, in the order the buyer made them. */
  let queue: Promise<unknown> = Promise.resolve();

  function enqueue<T>(call: () => Promise<T>): Promise<T> {
    const run = queue.then(call);
    queue = run.catch(() => undefined);
    return run;
  }

  function event(): Event {
    const selected = useEventsStore().selected;

    if (selected === null) throw new Error("Evento não selecionado");

    return selected;
  }

  /**
   * Why `amount` more of the type can't join the Cart, if they can't. The buyer's cap counts the tickets of seats and
   * General sectors together, seats still waiting on their hold included.
   */
  function refusal(type: TicketType, amount: number): Refusal | null {
    const tickets = lines.value.reduce((sum, l) => sum + ticketsOf(l.type), 0) + waiting.value.length;

    if (tickets + ticketsOf(type, amount) > event().quantidade_ingresso_venda_online) return "full";

    const ofType = lines.value.filter(l => l.type.id === type.id).length + waiting.value.filter(w => w.typeId === type.id).length;

    if (type.max_per_user !== null && ofType + amount > type.max_per_user) return "limit";

    return null;
  }

  /** Holds the seat, then adds it to the Cart with its type. A seat already in the Cart, or on its way, is left as it is. */
  async function addSeat(sector: Sector, section: Section, seat: Seat, type: TicketType): Promise<AddSeatResult> {
    if (lines.value.some(l => l.seat?.id === seat.id) || pending.value.includes(seat.id)) return "added";

    const refused = refusal(type, 1);
    if (refused !== null) return refused;

    eventId.value = event().id;
    const holdOn = eventId.value;
    waiting.value.push({ seatId: seat.id, typeId: type.id });

    try {
      return await enqueue(async () => {
        let held: HoldResponse;

        try {
          held = await holdSeat(holdOn, seat.id);
        } catch (error) {
          if (isTaken(error, seat)) return "taken";

          throw error;
        }

        lines.value.push({ key: seat.id, sector, section, type, seat });
        expiresAt.value = held.expires_at;

        return "added";
      });
    } finally {
      waiting.value = waiting.value.filter(w => w.seatId !== seat.id);
    }
  }

  /** Adds the amount of a General sector's type, one line per ticket. */
  function addGeneral(sector: Sector, section: Section, type: TicketType, amount: number): AddGeneralResult {
    const refused = refusal(type, amount);
    if (refused !== null) return refused;

    eventId.value = event().id;

    for (let i = 0; i < amount; i++) {
      lines.value.push({ key: `${type.id}:${Date.now()}:${++added}`, sector, section, type });
    }

    return "added";
  }

  /** How far the type's stepper goes on a General sector's sheet, so that the Cart takes what it picks. */
  function sheetMax(type: TicketType, pick: GeneralPick): number {
    return generalMax(type, pick, lines.value, event().quantidade_ingresso_venda_online, waiting.value.map(w => w.typeId));
  }

  /** Takes the ticket out of the Cart, releasing its seat's hold. A seat whose release fails comes back. */
  async function remove(line: CartLine): Promise<void> {
    lines.value = lines.value.filter(l => l.key !== line.key);

    const seat = line.seat;
    if (seat === undefined) return;

    const releaseOn = event().id;
    let released;

    try {
      released = await enqueue(() => releaseSeat(releaseOn, seat.id));
    } catch (error) {
      // Still held, so still the buyer's
      lines.value.push(line);
      throw error;
    }

    expiresAt.value = released.expires_at;
  }

  /** The hold ran out: the seats leave the Cart, General tickets stay. Returns the lines dropped. */
  function expire(): CartLine[] {
    const dropped = lines.value.filter(line => line.seat !== undefined);

    lines.value = lines.value.filter(line => line.seat === undefined);
    expiresAt.value = null;

    return dropped;
  }

  /** Takes out the seats the buyer no longer holds. Returns the lines dropped. */
  function dropSeats(lost: (seat: Seat) => boolean): CartLine[] {
    const dropping = (line: CartLine) => line.seat !== undefined && lost(line.seat);
    const dropped = lines.value.filter(dropping);

    lines.value = lines.value.filter(line => !dropping(line));

    return dropped;
  }

  /**
   * Keeps, from a Cart that came back after a reload, only the seats the buyer still holds. Returns the lines dropped.
   *
   * @param held Ids of the seats the buyer holds now.
   */
  function restore(held: string[]): CartLine[] {
    return dropSeats(seat => !held.includes(seat.id));
  }

  /**
   * Brings a Cart that came back, after a reload or from another step, in line with the API: the seats the buyer no
   * longer holds leave it, and the countdown takes the holds' expiry. Returns the lines dropped.
   */
  async function resume(): Promise<CartLine[]> {
    if (!holdsSeats.value || !useUserStore().logged) return [];

    const resumeOn = event().id;
    const held = await enqueue(() => getHeldSeats(resumeOn));

    expiresAt.value = held.expires_at;

    return restore(held.seats);
  }

  /**
   * Takes out the seats a Section's grid, just loaded, shows someone else has: their hold ran out and another buyer
   * took them, so there's no hold left to release. The grid marks only a logged-in buyer's own holds, so nothing is
   * taken out for a buyer who isn't. Returns the lines dropped.
   */
  function dropTaken(rows: Row[]): CartLine[] {
    const user = useUserStore();
    if (!user.logged) return [];

    const byId = new Map(rows.flatMap(row => row.seats ?? []).map(seat => [seat.id, seat]));

    return dropSeats((seat) => {
      const now = byId.get(seat.id);
      return now !== undefined && now.status !== "available" && now.user_id !== user.data.id;
    });
  }

  /** Empties the Cart, leaving any hold to run out on the API. */
  function clear(): void {
    lines.value = [];
    expiresAt.value = null;
    eventId.value = null;
  }

  // A Cart saved on another event's page has nothing to do with this one
  watch(() => useEventsStore().selected?.id, (id) => {
    if (id !== undefined && eventId.value !== null && id !== eventId.value) clear();
  }, { immediate: true });

  /** The event's sectors with each type's amount from the Cart, for the reservation. */
  function toReservation(sectors: Sector[]): Sector[] {
    return reservationSectors(lines.value, sectors);
  }

  /** One ticket per line, the first in the buyer's name. */
  function toTickets(): NominalTicket[] {
    return ticketsFromLines(lines.value, useUserStore().data);
  }

  return {
    lines,
    expiresAt,
    eventId,
    pending,
    count,
    holdsSeats,
    groups,
    subtotal,
    fees,
    addSeat,
    addGeneral,
    sheetMax,
    remove,
    expire,
    restore,
    resume,
    dropTaken,
    clear,
    toReservation,
    toTickets,
  };
}, { persist: { pick: ["lines", "expiresAt", "eventId"] } });
